/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, August 28th 2022, 1:02:14 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/**
 * This module resolves global configs from both the process.env object accessed during the build process and the web page's
 * window object accessed when the app is rendered on the browser.
 *
 * The motivation behind using the window object in addition to process.env is to handle multi-target environments while ensuring
 * the integrity of the code. So, for variables that are only used for display purposes, we can use process.env as our script at
 * docker/start-onestop-admin.sh will replace TMP values created during build to actual values in the container during runtime.
 * But for variables that are used in computation e.g. if x === 2, we have to ensure that the TMP_ values are not wrongly using to
 * return a result during buildtime, thus the reason for using the window object by setting vars in the public/global-config.js. This
 * is possible because create-react-app does not include public folder and the window object during build thus we can ensure the values
 * pick from there will always be during rendering of the app.
 *
 * Not an ideal scenario but pretty much good enough for now. A better approach would be to use a config.json and fetch it before
 * the app renders in index.ts (this is a common approach that is more secure).
 *
 * Some more info on the multi-target environment problem with create-react-app can be found at:
 *
 * https://profinit.eu/en/blog/build-once-deploy-many-in-react-dynamic-configuration-properties/
 * https://cevo.com.au/post/how-to-build-once-and-deploy-many-for-react-app-in-ci-cd/
 * https://blog.mikesir87.io/2021/07/build-once-deploy-everywhere-for-spas/
 * https://www.daniellittle.dev/environment-settings-for-build-once-packages-in-react
 *
 */

import { getEnv } from "./utils";

export const ONESTOP_DOMAIN_NAME = "onestop.site";
export const CUSTOMER_SERVICE_EMAIL = "info@onestop.site";
export const SALES_EMAIL = "sales@onestop.site";
export const PRIVACY_POLICY_URL = "https://onestop.site/privacy-policy/";
export const TERMS_OF_SERVICE_URL = "https://onestop.site/terms-of-service/";
export const CONTACT_US_URL = "https://onestop.site/contact-us";
export const TRIAL_DAYS = process.env.REACT_APP_ONESTOP_SUBSCRIPTION_TRIAL_PERIOD;
// export const CHOOSE_SUBSCRIPTION_SUBTITLE = `Free ${TRIAL_DAYS}-day trial. No credit card needed`;
export const CHOOSE_SUBSCRIPTION_SUBTITLE = "Get started with a free plan. No credit card needed";
export const ADMIN_DOCUMENTATION_URL =
  process.env.REACT_APP_ADMIN_DOCUMENTATION_URL ?? "https://docs.onestop-online.com/";
type TDEPLOYMENT_MODE = "SAAS" | "ON_PREMISE";
export const ADMIN_PORTAL_DEPLOYMENT_MODE: TDEPLOYMENT_MODE =
  process.env.NODE_ENV === "production"
    ? (window as any)?.REACT_APP_DEPLOYMENT_MODE
    : (getEnv("REACT_APP_DEPLOYMENT_MODE") as TDEPLOYMENT_MODE);
export const ALLOW_MULTI_ORGANIZATION_FOR_ON_PREMISE =
  getEnv("REACT_APP_ALLOW_MULTI_ORGANIZATION_FOR_ON_PREMISE") === "true" ? true : false;
